/**
 * The industry vocabulary — §17.5.
 *
 * **Why this file exists.** Both people-data vendors filter on a CLOSED
 * industry vocabulary — the LinkedIn taxonomy — and neither tolerates a value
 * outside it. Amplemarket rejects the ENTIRE request:
 *
 *   HTTP 400 {"code":"unsupported_value",
 *             "detail":"'Enterprise Software' is not a supported industry.",
 *             "source":{"pointer":"/company_industries"}}
 *
 * ContactOut is quieter and therefore worse: an unknown `industry[]` value
 * returns HTTP 200 with zero results (verified 2026-09-03), so a typo would
 * read as "nobody in that market" rather than as a failed filter.
 *
 * A persona's industries are free text typed by a human in the Library, so
 * "Enterprise Software" — a perfectly reasonable thing to write, and not a
 * supported value — took down company discovery completely. Seniorities and
 * size bands were already filtered against their vocabularies for exactly
 * this reason (see `queryForPersona`); industries were passed through raw.
 *
 * Sourced from docs.amplemarket.com/api-reference/supported-industries and
 * checked against ContactOut's accepted-values sheet: ContactOut's 492 values
 * contain these 433 exactly (its 59 extras are legacy LinkedIn names such as
 * "Computer Hardware"; nothing sends them). It moves rarely — but it CAN move,
 * which is why an unsupported value is reported to the human with suggestions
 * rather than silently dropped, and why a vendor's own error is surfaced
 * verbatim when one slips through anyway.
 */

import { VENDOR_FILTER_LIMITS } from './vendors';

/**
 * @deprecated Use `VENDOR_FILTER_LIMITS.amplemarket`, or better the selected
 * provider's `capabilities.maxFilterOptions`. Kept for one release so older
 * importers move in their own commit.
 *
 * Amplemarket's per-filter option ceiling, confirmed against the live API:
 *
 *   'company_industries' exceeds the maximum options per filter (10).
 *
 * It applies to `person_seniorities` too (14 values → 400), though a persona
 * only ever produces one. `person_titles` is NOT capped — 11 was accepted —
 * which matters, because titles are the one list a rep writes freely.
 */
export const AMPLEMARKET_MAX_FILTER_OPTIONS: number = VENDOR_FILTER_LIMITS.amplemarket;

export const INDUSTRIES: readonly string[] = [
  'Abrasives and Nonmetallic Minerals Manufacturing',
  'Accessible Architecture and Design',
  'Accessible Hardware Manufacturing',
  'Accommodation and Food Services',
  'Accounting',
  'Administration of Justice',
  'Administrative and Support Services',
  'Advertising Services',
  'Agricultural Chemical Manufacturing',
  'Agriculture, Construction, Mining Machinery Manufacturing',
  'Air, Water, and Waste Program Management',
  'Airlines and Aviation',
  'Alternative Dispute Resolution',
  'Alternative Fuel Vehicle Manufacturing',
  'Alternative Medicine',
  'Ambulance Services',
  'Amusement Parks and Arcades',
  'Animal Feed Manufacturing',
  'Animation and Post-production',
  'Apparel Manufacturing',
  'Appliances, Electrical, and Electronics Manufacturing',
  'Architectural and Structural Metal Manufacturing',
  'Architecture and Planning',
  'Armed Forces',
  'Artificial Rubber and Synthetic Fiber Manufacturing',
  'Artists and Writers',
  'Audio and Video Equipment Manufacturing',
  'Automation Machinery Manufacturing',
  'Aviation and Aerospace Component Manufacturing',
  'Baked Goods Manufacturing',
  'Banking',
  'Bars, Taverns, and Nightclubs',
  'Bed-and-Breakfasts, Hostels, Homestays',
  'Beverage Manufacturing',
  'Biomass Electric Power Generation',
  'Biotechnology Research',
  'Blockchain Services',
  'Blogs',
  'Boilers, Tanks, and Shipping Container Manufacturing',
  'Book Publishing',
  'Book and Periodical Publishing',
  'Breweries',
  'Broadcast Media Production and Distribution',
  'Building Construction',
  'Building Equipment Contractors',
  'Building Finishing Contractors',
  'Building Structure and Exterior Contractors',
  'Business Consulting and Services',
  'Business Content',
  'Business Intelligence Platforms',
  'Cable and Satellite Programming',
  'Capital Markets',
  'Caterers',
  'Chemical Manufacturing',
  'Chemical Raw Materials Manufacturing',
  'Child Day Care Services',
  'Chiropractors',
  'Circuses and Magic Shows',
  'Civic and Social Organizations',
  'Civil Engineering',
  'Claims Adjusting, Actuarial Services',
  'Clay and Refractory Products Manufacturing',
  'Climate Data and Analytics',
  'Climate Technology Product Manufacturing',
  'Coal Mining',
  'Collection Agencies',
  'Commercial and Industrial Equipment Rental',
  'Commercial and Industrial Machinery Maintenance',
  'Commercial and Service Industry Machinery Manufacturing',
  'Communications Equipment Manufacturing',
  'Community Development and Urban Planning',
  'Community Services',
  'Computer Games',
  'Computer Hardware Manufacturing',
  'Computer Networking Products',
  'Computer and Network Security',
  'Computers and Electronics Manufacturing',
  'Conservation Programs',
  'Construction',
  'Construction Hardware Manufacturing',
  'Consumer Goods Rental',
  'Consumer Services',
  'Correctional Institutions',
  'Cosmetology and Barber Schools',
  'Courts of Law',
  'Credit Intermediation',
  'Cutlery and Handtool Manufacturing',
  'Dairy Product Manufacturing',
  'Dance Companies',
  'Data Infrastructure and Analytics',
  'Data Security Software Products',
  'Defense and Space Manufacturing',
  'Dentists',
  'Design Services',
  'Desktop Computing Software Products',
  'Digital Accessibility Services',
  'Distilleries',
  'E-Learning Providers',
  'Economic Programs',
  'Education',
  'Education Administration Programs',
  'Electric Lighting Equipment Manufacturing',
  'Electric Power Generation',
  'Electric Power Transmission, Control, and Distribution',
  'Electrical Equipment Manufacturing',
  'Electronic and Precision Equipment Maintenance',
  'Embedded Software Products',
  'Emergency and Relief Services',
  'Engineering Services',
  'Engines and Power Transmission Equipment Manufacturing',
  'Entertainment Providers',
  'Environmental Quality Programs',
  'Environmental Services',
  'Equipment Rental Services',
  'Events Services',
  'Executive Offices',
  'Executive Search Services',
  'Fabricated Metal Products',
  'Facilities Services',
  'Family Planning Centers',
  'Farming',
  'Farming, Ranching, Forestry',
  'Fashion Accessories Manufacturing',
  'Financial Services',
  'Fine Arts Schools',
  'Fire Protection',
  'Fisheries',
  'Flight Training',
  'Food and Beverage Manufacturing',
  'Food and Beverage Retail',
  'Food and Beverage Services',
  'Footwear Manufacturing',
  'Footwear and Leather Goods Repair',
  'Forestry and Logging',
  'Fossil Fuel Electric Power Generation',
  'Freight and Package Transportation',
  'Fruit and Vegetable Preserves Manufacturing',
  'Fuel Cell Manufacturing',
  'Fundraising',
  'Funds and Trusts',
  'Furniture and Home Furnishings Manufacturing',
  'Gambling Facilities and Casinos',
  'Geothermal Electric Power Generation',
  'Glass Product Manufacturing',
  'Glass, Ceramics and Concrete Manufacturing',
  'Golf Courses and Country Clubs',
  'Government Administration',
  'Government Relations Services',
  'Graphic Design',
  'Ground Passenger Transportation',
  'HVAC and Refrigeration Equipment Manufacturing',
  'Health and Human Services',
  'Higher Education',
  'Highway, Street, and Bridge Construction',
  'Historical Sites',
  'Holding Companies',
  'Home Health Care Services',
  'Horticulture',
  'Hospitality',
  'Hospitals',
  'Hospitals and Health Care',
  'Hotels and Motels',
  'Household Appliance Manufacturing',
  'Household Services',
  'Household and Institutional Furniture Manufacturing',
  'Housing Programs',
  'Housing and Community Development',
  'Human Resources Services',
  'Hydroelectric Power Generation',
  'IT Services and IT Consulting',
  'IT System Custom Software Development',
  'IT System Data Services',
  'IT System Design Services',
  'IT System Installation and Disposal',
  'IT System Operations and Maintenance',
  'IT System Testing and Evaluation',
  'IT System Training and Support',
  'Individual and Family Services',
  'Industrial Machinery Manufacturing',
  'Industry Associations',
  'Information Services',
  'Insurance',
  'Insurance Agencies and Brokerages',
  'Insurance Carriers',
  'Insurance and Employee Benefit Funds',
  'Interior Design',
  'International Affairs',
  'International Trade and Development',
  'Internet Marketplace Platforms',
  'Internet News',
  'Internet Publishing',
  'Interurban and Rural Bus Services',
  'Investment Advice',
  'Investment Banking',
  'Investment Management',
  'Janitorial Services',
  'Landscaping Services',
  'Language Schools',
  'Laundry and Drycleaning Services',
  'Law Enforcement',
  'Law Practice',
  'Leasing Non-residential Real Estate',
  'Leasing Residential Real Estate',
  'Leather Product Manufacturing',
  'Legal Services',
  'Legislative Offices',
  'Libraries',
  'Lime and Gypsum Products Manufacturing',
  'Loan Brokers',
  'Machinery Manufacturing',
  'Magnetic and Optical Media Manufacturing',
  'Manufacturing',
  'Maritime Transportation',
  'Market Research',
  'Marketing Services',
  'Mattress and Blinds Manufacturing',
  'Measuring and Control Instrument Manufacturing',
  'Meat Products Manufacturing',
  'Media Production',
  'Media and Telecommunications',
  'Medical Equipment Manufacturing',
  'Medical Practices',
  'Medical and Diagnostic Laboratories',
  'Mental Health Care',
  'Metal Ore Mining',
  'Metal Treatments',
  'Metal Valve, Ball, and Roller Manufacturing',
  'Metalworking Machinery Manufacturing',
  'Military and International Affairs',
  'Mining',
  'Mobile Computing Software Products',
  'Mobile Food Services',
  'Mobile Gaming Apps',
  'Motor Vehicle Manufacturing',
  'Motor Vehicle Parts Manufacturing',
  'Movies and Sound Recording',
  'Movies, Videos, and Sound',
  'Museums',
  'Museums, Historical Sites, and Zoos',
  'Musicians',
  'Nanotechnology Research',
  'Natural Gas Distribution',
  'Natural Gas Extraction',
  'Newspaper Publishing',
  'Non-profit Organizations',
  'Nonmetallic Mineral Mining',
  'Nonresidential Building Construction',
  'Nuclear Electric Power Generation',
  'Nursing Homes and Residential Care Facilities',
  'Office Administration',
  'Office Furniture and Fixtures Manufacturing',
  'Oil Extraction',
  'Oil and Coal Product Manufacturing',
  'Oil and Gas',
  'Oil, Gas, and Mining',
  'Online Audio and Video Media',
  'Online and Mail Order Retail',
  'Operations Consulting',
  'Optometrists',
  'Outpatient Care Centers',
  'Outsourcing and Offshoring Consulting',
  'Packaging and Containers Manufacturing',
  'Paint, Coating, and Adhesive Manufacturing',
  'Paper and Forest Product Manufacturing',
  'Pension Funds',
  'Performing Arts',
  'Performing Arts and Spectator Sports',
  'Periodical Publishing',
  'Personal Care Product Manufacturing',
  'Personal Care Services',
  'Personal and Laundry Services',
  'Pet Services',
  'Pharmaceutical Manufacturing',
  'Philanthropic Fundraising Services',
  'Photography',
  'Physical, Occupational and Speech Therapists',
  'Physicians',
  'Pipeline Transportation',
  'Plastics Manufacturing',
  'Plastics and Rubber Product Manufacturing',
  'Political Organizations',
  'Postal Services',
  'Primary Metal Manufacturing',
  'Primary and Secondary Education',
  'Printing Services',
  'Professional Organizations',
  'Professional Services',
  'Professional Training and Coaching',
  'Public Assistance Programs',
  'Public Health',
  'Public Policy Offices',
  'Public Relations and Communications Services',
  'Public Safety',
  'Racetracks',
  'Radio and Television Broadcasting',
  'Rail Transportation',
  'Railroad Equipment Manufacturing',
  'Ranching',
  'Ranching and Fisheries',
  'Real Estate',
  'Real Estate Agents and Brokers',
  'Real Estate and Equipment Rental Services',
  'Recreational Facilities',
  'Regenerative Design',
  'Religious Institutions',
  'Renewable Energy Equipment Manufacturing',
  'Renewable Energy Power Generation',
  'Renewable Energy Semiconductor Manufacturing',
  'Repair and Maintenance',
  'Research Services',
  'Residential Building Construction',
  'Restaurants',
  'Retail',
  'Retail Apparel and Fashion',
  'Retail Appliances, Electrical, and Electronic Equipment',
  'Retail Art Dealers',
  'Retail Art Supplies',
  'Retail Books and Printed News',
  'Retail Building Materials and Garden Equipment',
  'Retail Florists',
  'Retail Furniture and Home Furnishings',
  'Retail Gasoline',
  'Retail Groceries',
  'Retail Health and Personal Care Products',
  'Retail Luxury Goods and Jewelry',
  'Retail Motor Vehicles',
  'Retail Musical Instruments',
  'Retail Office Equipment',
  'Retail Office Supplies and Gifts',
  'Retail Pharmacies',
  'Retail Recyclable Materials & Used Merchandise',
  'Reupholstery and Furniture Repair',
  'Robot Manufacturing',
  'Robotics Engineering',
  'Rubber Products Manufacturing',
  'Satellite Telecommunications',
  'Savings Institutions',
  'School and Employee Bus Services',
  'Seafood Product Manufacturing',
  'Secretarial Schools',
  'Securities and Commodity Exchanges',
  'Security Guards and Patrol Services',
  'Security Systems Services',
  'Security and Investigations',
  'Semiconductor Manufacturing',
  'Services for Renewable Energy',
  'Services for the Elderly and Disabled',
  'Sheet Music Publishing',
  'Shipbuilding',
  'Shuttles and Special Needs Transportation Services',
  'Sightseeing Transportation',
  'Skiing Facilities',
  'Smart Meter Manufacturing',
  'Soap and Cleaning Product Manufacturing',
  'Social Networking Platforms',
  'Software Development',
  'Solar Electric Power Generation',
  'Sound Recording',
  'Space Research and Technology',
  'Specialty Trade Contractors',
  'Spectator Sports',
  'Sporting Goods Manufacturing',
  'Sports Teams and Clubs',
  'Sports and Recreation Instruction',
  'Spring and Wire Product Manufacturing',
  'Staffing and Recruiting',
  'Steam and Air-Conditioning Supply',
  'Strategic Management Services',
  'Subdivision of Land',
  'Sugar and Confectionery Product Manufacturing',
  'Surveying and Mapping Services',
  'Taxi and Limousine Services',
  'Technical and Vocational Training',
  'Technology, Information and Internet',
  'Technology, Information and Media',
  'Telecommunications',
  'Telecommunications Carriers',
  'Telephone Call Centers',
  'Temporary Help Services',
  'Textile Manufacturing',
  'Theater Companies',
  'Think Tanks',
  'Tobacco Manufacturing',
  'Translation and Localization',
  'Transportation Equipment Manufacturing',
  'Transportation Programs',
  'Transportation, Logistics, Supply Chain and Storage',
  'Travel Arrangements',
  'Truck Transportation',
  'Trusts and Estates',
  'Turned Products and Fastener Manufacturing',
  'Urban Transit Services',
  'Utilities',
  'Utilities Administration',
  'Utility System Construction',
  'Vehicle Repair and Maintenance',
  'Venture Capital and Private Equity Principals',
  'Veterinary Services',
  'Vocational Rehabilitation Services',
  'Warehousing and Storage',
  'Waste Collection',
  'Waste Treatment and Disposal',
  'Water Supply and Irrigation Systems',
  'Water, Waste, Steam, and Air Conditioning Services',
  'Wellness and Fitness Services',
  'Wholesale',
  'Wholesale Alcoholic Beverages',
  'Wholesale Apparel and Sewing Supplies',
  'Wholesale Appliances, Electrical, and Electronics',
  'Wholesale Building Materials',
  'Wholesale Chemical and Allied Products',
  'Wholesale Computer Equipment',
  'Wholesale Drugs and Sundries',
  'Wholesale Food and Beverage',
  'Wholesale Footwear',
  'Wholesale Furniture and Home Furnishings',
  'Wholesale Hardware, Plumbing, Heating Equipment',
  'Wholesale Import and Export',
  'Wholesale Luxury Goods and Jewelry',
  'Wholesale Machinery',
  'Wholesale Metals and Minerals',
  'Wholesale Motor Vehicles and Parts',
  'Wholesale Paper Products',
  'Wholesale Petroleum and Petroleum Products',
  'Wholesale Photography Equipment and Supplies',
  'Wholesale Raw Farm Products',
  'Wholesale Recyclable Materials',
  'Wind Electric Power Generation',
  'Wineries',
  'Wireless Services',
  "Women's Handbag Manufacturing",
  'Wood Product Manufacturing',
  'Writing and Editing',
  'Zoos and Botanical Gardens',
] as const;

/** @deprecated Use `INDUSTRIES` — the same list; both vendors accept it. */
export const AMPLEMARKET_INDUSTRIES: readonly string[] = INDUSTRIES;

/** Lowercased lookup, built once. */
const BY_LOWER = new Map(INDUSTRIES.map((i) => [i.toLowerCase(), i]));

/**
 * Resolve one free-text industry to its canonical value, or null.
 *
 * Case- and whitespace-insensitive, because "financial services" typed into
 * the Library is the same instruction as "Financial Services" — and returning
 * the CANONICAL spelling matters: the API compares exactly.
 */
export function canonicalIndustry(value: string): string | null {
  return BY_LOWER.get(value.trim().toLowerCase()) ?? null;
}

/**
 * How a STORED industry value is spelled as a key of `industry_mappings`:
 * lowercased, underscores read as spaces, whitespace collapsed. HubSpot's
 * `COMPUTER_SOFTWARE` and a typed "Computer  software" are one question to
 * the mapper and one cached answer. `entities/industries.ts` holds the same
 * rule in SQL, and the two must move together.
 */
export function industryKey(raw: string): string {
  return raw.trim().toLowerCase().replace(/_/g, ' ').replace(/\s+/g, ' ');
}

/**
 * An industry as a person reads it, for a value that has no canonical name
 * yet. Vendor values are already prose ("Software Development"); HubSpot's
 * are its enum keys (`COMPUTER_SOFTWARE`), shown as words. The last line of
 * defence too: whatever reaches an email through `{{industry}}` passes here,
 * so a prospect never reads an enum key.
 */
export function humaniseIndustry(value: string): string {
  // `BANKING` is the taxonomy's "Banking" in another case: say it the taxonomy's way.
  const canonical = BY_LOWER.get(industryKey(value));
  if (canonical) return canonical;
  if (!/^[A-Z0-9_&]+$/.test(value)) return value;
  const words = value.toLowerCase().replace(/_/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Take a model's proposed translation and keep only what is real — §17.5.
 *
 * A language model asked to pick from a 434-item vocabulary will occasionally
 * return something plausible that is not in it ("SaaS", "Software & IT"), and
 * a single such value fails the whole Amplemarket request and finds nobody on
 * ContactOut. So the model's answer is a SUGGESTION that this function
 * ratifies: canonicalised where it matches, dropped and reported where it does
 * not, de-duplicated, and capped at `limit` — the caller passes the SELECTED
 * vendor's `capabilities.maxFilterOptions`; the default is Amplemarket's 10,
 * the stricter of the two, so a caller that has not asked the vendor is safe
 * on either.
 *
 * Pure and exported so the trust boundary is unit-testable without a model.
 */
export function validateMappedIndustries(
  proposed: readonly string[],
  limit: number = VENDOR_FILTER_LIMITS.amplemarket,
): { accepted: string[]; rejected: string[] } {
  const accepted: string[] = [];
  const rejected: string[] = [];

  for (const value of proposed) {
    if (typeof value !== 'string' || !value.trim()) continue;
    const canonical = canonicalIndustry(value);
    if (!canonical) {
      rejected.push(value.trim());
      continue;
    }
    if (!accepted.includes(canonical)) accepted.push(canonical);
  }

  return { accepted: accepted.slice(0, limit), rejected };
}

const STOPWORDS = new Set(['and', 'the', 'of', 'for', 'services', 'products', 'a', 'an']);

function tokens(value: string): string[] {
  return value
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((t) => t.length > 2 && !STOPWORDS.has(t));
}

/**
 * The nearest supported industries to something unsupported — so the message
 * a human gets is "did you mean Software Development?" rather than a wall of
 * 400 options or a bare refusal.
 *
 * Scored by shared significant tokens; a substring hit counts double, which is
 * what makes "Enterprise Software" surface "Software Development" ahead of
 * every other entry containing "enterprise".
 */
export function suggestIndustries(value: string, limit = 3): string[] {
  const wanted = tokens(value);
  if (wanted.length === 0) return [];

  return INDUSTRIES.map((candidate) => {
    const candidateTokens = tokens(candidate);
    // Compound words are one token to a human and two in the vocabulary:
    // "healthcare" has to be able to find "Hospitals and Health Care", or the
    // suggestion is worse than useless for the words people actually type.
    const joined = candidateTokens.join('');
    let score = 0;
    for (const token of wanted) {
      if (candidateTokens.includes(token) || joined.includes(token)) score += 2;
      else if (candidateTokens.some((c) => c.startsWith(token) || token.startsWith(c))) score += 1;
    }
    return { candidate, score };
  })
    .filter((row) => row.score > 0)
    .sort((a, b) => b.score - a.score || a.candidate.length - b.candidate.length)
    .slice(0, limit)
    .map((row) => row.candidate);
}
